import type { UserSession } from '@/types/user'

/*
 * The Amplitude SDK (~64 KB gzipped) is imported on first use, not with every page.
 * Tracking runs in production only, so other environments never download it.
 *
 * Events tracked before `initUser` are queued by the SDK and sent once `init` is done.
 */
let sdkImport: Promise<typeof import('@amplitude/analytics-browser')> | undefined
const loadSdk = () => (sdkImport ??= import('@amplitude/analytics-browser'))

const logLoadError = (error: unknown) => {
  console.error('Amplitude: failed to load the SDK', error)
}

// Fire-and-forget: load errors are handled here, so callers get nothing to await.
export function track(
  eventType: string,
  eventProperties?: Record<string, unknown>,
): void {
  loadSdk()
    .then((sdk) => sdk.track(eventType, eventProperties))
    .catch(logLoadError)
}

/**
 * Starts tracking for a user.
 * @description The userId (externalUserIdentifierId) is the relation between IAM user and Cognito user.
 */
export function initUser(apiKey: string, userId: string, userSession: UserSession): void {
  loadSdk()
    .then((sdk) => {
      sdk.init(apiKey, userId, {
        autocapture: {
          elementInteractions: true,
          pageViews: true,
          sessions: true,
        },
        serverZone: 'EU',
      })

      // Session Replay attaches to this same SDK instance, so replays share the
      // analytics device ID, session ID and serverZone. Loaded on demand, like the
      // SDK itself.
      import('@amplitude/plugin-session-replay-browser')
        .then(
          ({ sessionReplayPlugin }) =>
            sdk.add(sessionReplayPlugin({ debugMode: true, sampleRate: 1 })).promise,
        )
        .catch(logLoadError)

      // Setting amplitude grouping feature
      sdk.setGroup('Client', userSession.client.name)
      sdk.setGroup('Datagroup', userSession.datagroup.name)

      // Identifying the user
      const identifyEvent = new sdk.Identify()
      identifyEvent.set('user_email', userSession.user.email)
      identifyEvent.set('user_id', userId)
      identifyEvent.set('client', userSession.client.name)
      identifyEvent.set('datagroup', userSession.datagroup.name)
      sdk.identify(identifyEvent)
    })
    .catch(logLoadError)
}
